import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { List, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { IFilter } from 'types/app';
import { Desktop, Mobile, StickySidebar, MobileSubmenu, SidebarItem } from 'components';
import * as paths from 'paths';
import * as Links from 'Links';

const useStyles = makeStyles(theme => ({
  subMenuText: {
    paddingLeft: 18,
    margin: `${theme.spacing(3)}px 0 ${theme.spacing(1)}px`,
    letterSpacing: '2.8px',
    fontSize: theme.typography.pxToRem(10),
    color: theme.custom.palette.grey2,
    textTransform: 'uppercase',
  },
  wrapper: {
    borderRadius: '4px',
    backgroundColor: theme.custom.palette.grey6,
  },
}));

const AdminNav: FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const { pathname } = location;

  const primaryMenu: IFilter[] = [
    {
      component: Links.AdminAnalyticsOverview,
      isSelected: pathname.startsWith(paths.adminAnalytics),
      textNode: t('admin:analytics'),
      subMenu: [
        {
          component: Links.AdminAnalyticsOverview,
          isSelected: pathname.startsWith(paths.adminAnalyticsOverview),
          textNode: t('admin:analyticOverview'),
        },
        {
          component: Links.AdminAnalyticsGroups,
          isSelected: pathname.startsWith(paths.adminAnalyticsGroups),
          textNode: t('admin:analyticGroups'),
        },
        {
          component: Links.AdminAnalyticsUsers,
          isSelected: pathname.startsWith(paths.adminAnalyticsUsers),
          textNode: t('admin:analyticUsers'),
        },
        {
          component: Links.AdminAnalyticsPosts,
          isSelected: pathname.startsWith(paths.adminAnalyticsPosts),
          textNode: t('admin:analyticPosts'),
        },
        {
          component: Links.AdminAnalyticsShares,
          isSelected: pathname.startsWith(paths.adminAnalyticsShares),
          textNode: t('admin:analyticShares'),
        },
        {
          component: Links.AdminAnalyticsLeaderboards,
          isSelected: pathname.startsWith(paths.adminAnalyticsLeaderboards),
          textNode: t('admin:analyticLeaderboards'),
        },
      ],
    },
    {
      component: Links.AdminPeople,
      isSelected: pathname.startsWith(paths.adminPeople),
      textNode: t('admin:people'),
    },
    {
      component: Links.AdminGroups,
      isSelected: pathname === paths.adminGroups,
      textNode: t('admin:groups'),
    },
    {
      component: Links.AdminInvitations,
      isSelected: pathname === paths.adminInvitations,
      textNode: t('admin:invitations'),
    },
    {
      component: Links.AdminMessages,
      isSelected: pathname === paths.adminMessages,
      textNode: t('admin:messages'),
    },
    {
      component: Links.AdminContentEmails,
      isSelected: pathname.startsWith(paths.adminContentEmails),
      textNode: t('admin:contentEmails'),
    },
    {
      component: Links.AdminLeaderboards,
      isSelected: pathname === paths.adminLeaderboards,
      textNode: t('common:leaderboard_plural'),
    },
    {
      component: Links.AdminReports,
      isSelected: pathname === paths.adminReports,
      textNode: t('admin:reports'),
    },
    {
      component: Links.AdminSettingsDepartments,
      isSelected: pathname.startsWith(paths.adminSettings),
      textNode: t('admin:settings'),
      subMenu: [
        {
          component: Links.AdminSettingsDepartments,
          isSelected: pathname === paths.adminSettingsDepartments,
          textNode: t('admin:account'),
        },
        {
          component: Links.AdminSettingsCompanyOnboarding,
          isSelected:
            pathname === paths.adminSettingsCompanyOnboarding ||
            pathname === paths.adminSettingsCustomOnboarding,
          textNode: t('admin:onboarding'),
        },
        {
          component: Links.AdminSettingsUserSegments,
          isSelected: pathname === paths.adminSettingsUserSegments,
          textNode: t('common:segment_plural'),
        },
        {
          component: Links.AdminSettingsShareTracking,
          isSelected: pathname === paths.adminSettingsShareTracking,
          textNode: t('admin:params'),
        },
        {
          component: Links.AdminSettingsSharing,
          isSelected: pathname === paths.adminSettingsSharing,
          textNode: t('admin:sharing'),
        },
      ],
    },
  ];

  function getMobileTitle() {
    const selectedFilter = primaryMenu.find(item => item.isSelected);
    return selectedFilter ? selectedFilter.textNode : '';
  }

  function renderLink(filter: IFilter, index: number) {
    const { component, ...rest } = filter;

    if (!filter.subMenu) {
      return <SidebarItem key={index} link={component} {...rest} />;
    }

    return (
      <div
        key={index}
        className={classNames({
          [classes.wrapper]:
            rest.isSelected &&
            (pathname.includes(paths.adminAnalytics) || pathname.includes(paths.adminSettings)),
        })}>
        <SidebarItem link={component} {...rest} />
        {rest.isSelected &&
          filter.subMenu.map((item, itemIndex) => (
            <SidebarItem key={itemIndex} link={item.component} {...item} inset={true} />
          ))}
      </div>
    );
  }

  function renderDesktop() {
    return (
      <StickySidebar>
        <List component="nav">{primaryMenu.map(renderLink)}</List>
      </StickySidebar>
    );
  }

  function renderMobile() {
    return (
      <MobileSubmenu
        expanded={expanded}
        onToggle={isExpanded => setExpanded(isExpanded)}
        selected={<Typography variant="subtitle2">{getMobileTitle()}</Typography>}>
        <List component="nav">{primaryMenu.map(renderLink)}</List>
      </MobileSubmenu>
    );
  }

  return (
    <Fragment>
      <Desktop>{renderDesktop()}</Desktop>
      <Mobile>{renderMobile()}</Mobile>
    </Fragment>
  );
};

export default AdminNav;
